// Asset-Length input helpers — parse what the talent typed into the
// "Asset Length" field and format the committed value back into the field.
//
// The field commits on blur/Enter only (GitHub #2): while typing, the
// component keeps a local draft string and never touches the store, so
// "select all, type 18" no longer squashes the timeline to 1 s on the way
// to 18. Both helpers are pure so the quantisation rules live in one
// tested place instead of inside the component.

import { MAX_ASSET_DURATION } from '@shared/constants';

/** Raw input → seconds. Empty / non-numeric / ≤ 0 → 0 ("no target": the
 *  store falls through to bed/default). Clamped to MAX_ASSET_DURATION and
 *  rounded to the 0.1 s grid the timeline works on. */
export function parseAssetLength(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') return 0;
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  const secs = Math.min(parsed, MAX_ASSET_DURATION);
  return Math.round(secs * 10) / 10;
}

/** Seconds → canonical input string. 0 / negative renders as '' (empty field
 *  == no target). Whole numbers drop the decimal ("20"), everything else
 *  shows one place ("20.5"). */
export function formatAssetLength(secs: number): string {
  if (!secs || secs <= 0) return '';
  const rounded = Math.round(secs * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
